import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  Descriptions,
  Drawer,
  Empty,
  Input,
  Row,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from "antd";
import {
  ArrowLeftOutlined,
  CheckOutlined,
  CopyOutlined,
  HistoryOutlined,
  ReloadOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { api, type Paged } from "../../services/api";
import { RelativeTime } from "../../components/format";
import { useAuth } from "../../services/auth";
import {
  CONTENT_PLATFORMS,
  CONTENT_PLATFORM_META,
  formatPublishPackage,
  parseHashtags,
  type ContentDraftDetail,
  type ContentOutputSet,
  type ContentPlatform,
  type ContentPlatformOutput,
  type ContentRevision,
} from "./types";

const OPERATION_LABEL: Record<ContentRevision["operation"], string> = {
  initial_generate: "首次生成",
  group_regenerate: "整组重写",
  platform_rewrite: "单平台重写",
  manual_edit: "人工编辑",
  restore: "恢复历史",
};

function cloneOutputs(outputs: ContentOutputSet): ContentOutputSet {
  return JSON.parse(JSON.stringify(outputs)) as ContentOutputSet;
}

export function ContentDraftPage() {
  const { id = "" } = useParams();
  const { message, modal } = App.useApp();
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const writable = can("content.write");
  const [outputs, setOutputs] = useState<ContentOutputSet | null>(null);
  const [rewriteInstructions, setRewriteInstructions] = useState<Partial<Record<ContentPlatform, string>>>({});
  const [reason, setReason] = useState("");
  const [historyOpen, setHistoryOpen] = useState(false);

  const detail = useQuery({
    queryKey: ["socialContentDraft", id],
    queryFn: () => api.get<ContentDraftDetail>(`/api/v1/admin/content-drafts/${id}`),
    enabled: Boolean(id),
  });
  const revisions = useQuery({
    queryKey: ["socialContentRevisions", id],
    queryFn: () => api.get<Paged<ContentRevision>>(`/api/v1/admin/content-drafts/${id}/revisions`, { page: 1, pageSize: 100 }),
    enabled: Boolean(id),
  });

  const current = detail.data?.currentRevision ?? null;
  useEffect(() => {
    setOutputs(current?.outputs ? cloneOutputs(current.outputs) : null);
    setRewriteInstructions({});
    setReason("");
  }, [current?.id]);

  const dirty = useMemo(
    () => Boolean(outputs && current?.outputs && JSON.stringify(outputs) !== JSON.stringify(current.outputs)),
    [outputs, current?.outputs],
  );

  const acceptResult = (data: ContentDraftDetail, success: string) => {
    queryClient.setQueryData(["socialContentDraft", id], data);
    void queryClient.invalidateQueries({ queryKey: ["socialContentRevisions", id] });
    void queryClient.invalidateQueries({ queryKey: ["/api/v1/admin/content-drafts"] });
    message.success(success);
  };

  const generation = useMutation({
    mutationFn: (payload: { targetPlatform?: ContentPlatform; instruction?: string }) =>
      api.post<ContentDraftDetail>(`/api/v1/admin/content-drafts/${id}/generations`, {
        baseRevisionId: current?.id ?? null,
        targetPlatform: payload.targetPlatform,
        instruction: payload.instruction,
      }),
    onSuccess: (data, variables) => acceptResult(data, variables.targetPlatform ? `${CONTENT_PLATFORM_META[variables.targetPlatform].label}已重写` : "四个平台文案已生成"),
    onError: (error) => {
      message.error((error as Error).message);
      void revisions.refetch();
    },
  });

  const save = useMutation({
    mutationFn: () => api.patch<ContentDraftDetail>(`/api/v1/admin/content-drafts/${id}`, {
      baseRevisionId: current?.id,
      outputs,
      reason,
    }),
    onSuccess: (data) => acceptResult(data, "人工修改已保存为新版本"),
    onError: (error) => message.error((error as Error).message),
  });

  const finalize = useMutation({
    mutationFn: () => api.post<ContentDraftDetail>(`/api/v1/admin/content-drafts/${id}/finalize`, {
      baseRevisionId: current?.id,
      reason,
    }),
    onSuccess: (data) => acceptResult(data, "当前版本已定稿"),
    onError: (error) => message.error((error as Error).message),
  });

  const restore = useMutation({
    mutationFn: (revisionId: string) => api.post<ContentDraftDetail>(`/api/v1/admin/content-drafts/${id}/restore`, {
      baseRevisionId: current?.id,
      revisionId,
      reason: reason || "恢复历史发布文案版本",
    }),
    onSuccess: (data) => acceptResult(data, "历史版本已恢复为新的当前版本"),
    onError: (error) => message.error((error as Error).message),
  });

  const busy = generation.isPending || save.isPending || finalize.isPending || restore.isPending;

  const updateOutput = (platform: ContentPlatform, patch: Partial<ContentPlatformOutput>) => {
    setOutputs((previous) => previous ? {
      ...previous,
      [platform]: { ...previous[platform], ...patch },
    } : previous);
  };

  const copy = async (platform: ContentPlatform) => {
    if (!outputs) return;
    try {
      await navigator.clipboard.writeText(formatPublishPackage(outputs[platform]));
      message.success(`${CONTENT_PLATFORM_META[platform].label}文案已复制`);
    } catch {
      message.error("复制失败，请手动选择文本复制");
    }
  };

  const rewrite = (platform: ContentPlatform) => {
    const instruction = rewriteInstructions[platform]?.trim();
    if (!instruction) {
      message.warning("先写一句修改意见");
      return;
    }
    generation.mutate({ targetPlatform: platform, instruction });
  };

  const regenerateAll = () => {
    modal.confirm({
      title: "重新生成四个平台文案？",
      content: "会产生一个新版本，当前版本仍会留在历史中。",
      okText: "重新生成",
      onOk: () => generation.mutate({}),
    });
  };

  const historyColumns: ColumnsType<ContentRevision> = [
    { title: "版本", dataIndex: "sequence", width: 72, render: (value: number) => `#${value}` },
    {
      title: "操作",
      width: 130,
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <span>{OPERATION_LABEL[row.operation]}</span>
          {row.targetPlatform && <Typography.Text type="secondary" style={{ fontSize: 12 }}>{CONTENT_PLATFORM_META[row.targetPlatform].label}</Typography.Text>}
        </Space>
      ),
    },
    {
      title: "状态",
      width: 92,
      render: (_, row) => <Tag color={row.status === "succeeded" ? "green" : row.status === "failed" ? "red" : "processing"}>{row.status === "succeeded" ? "成功" : row.status === "failed" ? "失败" : "生成中"}</Tag>,
    },
    {
      title: "说明",
      render: (_, row) => row.errorMessage
        ? <Typography.Text type="danger">{row.errorMessage}</Typography.Text>
        : <Typography.Text>{row.instruction || "—"}</Typography.Text>,
    },
    {
      title: "成本/耗时",
      width: 130,
      render: (_, row) => row.costCents === null ? "—" : `${row.costCents.toFixed(4)} 分 · ${row.latencyMs ?? 0}ms`,
    },
    { title: "时间", width: 150, render: (_, row) => <RelativeTime value={row.createdAt} /> },
    {
      title: "操作",
      width: 80,
      render: (_, row) => row.status === "succeeded" && row.outputs && row.id !== current?.id ? (
        <Button
          type="link"
          size="small"
          disabled={!writable || !current || dirty || busy}
          onClick={() => modal.confirm({
            title: `恢复版本 #${row.sequence}？`,
            content: "历史不会被覆盖，会新增一条恢复版本。",
            okText: "恢复",
            onOk: () => restore.mutate(row.id),
          })}
        >恢复</Button>
      ) : null,
    },
  ];

  if (detail.isLoading) return <div style={{ display: "grid", placeItems: "center", minHeight: 360 }}><Spin size="large" /></div>;
  if (detail.error || !detail.data) return <Alert type="error" showIcon message={(detail.error as Error | null)?.message ?? "草稿不存在"} />;
  const draft = detail.data;

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Card styles={{ body: { padding: 16 } }}>
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          <Space wrap style={{ justifyContent: "space-between", width: "100%" }}>
            <Space>
              <Link to="/content"><Button icon={<ArrowLeftOutlined />} /></Link>
              <Typography.Title level={4} style={{ margin: 0 }}>内容发布草稿</Typography.Title>
              <Tag color={draft.status === "finalized" ? "green" : "blue"}>{draft.status === "finalized" ? "已定稿" : "草稿"}</Tag>
              {current && <Tag>版本 #{current.sequence}</Tag>}
            </Space>
            <Space wrap>
              <Button icon={<HistoryOutlined />} onClick={() => setHistoryOpen(true)}>版本历史</Button>
              {current && writable && (
                <Button icon={<ReloadOutlined />} disabled={dirty || busy} loading={generation.isPending} onClick={regenerateAll}>
                  整组重写
                </Button>
              )}
            </Space>
          </Space>
          <Descriptions size="small" column={{ xs: 1, sm: 2, lg: 4 }}>
            <Descriptions.Item label="角色">{draft.characterName}</Descriptions.Item>
            <Descriptions.Item label="角色设定">v{draft.characterVersion}</Descriptions.Item>
            <Descriptions.Item label="创建时间"><RelativeTime value={draft.createdAt} /></Descriptions.Item>
            <Descriptions.Item label="最近更新"><RelativeTime value={draft.updatedAt} /></Descriptions.Item>
          </Descriptions>
          <div>
            <Typography.Text type="secondary">发布需求</Typography.Text>
            <Typography.Paragraph style={{ margin: "4px 0 0", whiteSpace: "pre-wrap" }}>{draft.brief}</Typography.Paragraph>
          </div>
          {!draft.contextSnapshot.song && (
            <Alert
              type="warning"
              showIcon
              message="这份草稿创建时没有关联歌曲"
              description="素材快照不会在生成后原地改变。如需歌词上下文，请新建草稿，在发布需求中输入 @ 并从歌曲自动补全列表点选；看到“已关联歌曲”标签后再创建。"
            />
          )}
          {(draft.contextSnapshot.plan || draft.contextSnapshot.moments.length > 0 || draft.contextSnapshot.song) && (
            <Alert
              type="info"
              showIcon
              message="本稿固定使用的公开角色素材"
              description={(
                <Space direction="vertical" size={4}>
                  {draft.contextSnapshot.plan && <span>今日设定 {draft.contextSnapshot.plan.dateString}：{draft.contextSnapshot.plan.plan}</span>}
                  {draft.contextSnapshot.moments.map((moment) => <span key={moment.id}>梦灵动态：{moment.factSummary}</span>)}
                  {draft.contextSnapshot.song && (
                    <div>
                      <span>
                        @歌曲：{draft.contextSnapshot.song.title}
                        {draft.contextSnapshot.song.originalArtists.length > 0 ? ` · 原唱 ${draft.contextSnapshot.song.originalArtists.join(" / ")}` : ""}
                        {` · ${draft.contextSnapshot.song.lyrics?.length ?? 0} 字歌词已固定到生成上下文`}
                      </span>
                      {draft.contextSnapshot.song.lyrics && (
                        <details style={{ marginTop: 4 }}>
                          <summary style={{ cursor: "pointer" }}>查看歌词快照</summary>
                          <Typography.Paragraph style={{ whiteSpace: "pre-wrap", margin: "8px 0 0" }}>
                            {draft.contextSnapshot.song.lyrics}
                          </Typography.Paragraph>
                        </details>
                      )}
                    </div>
                  )}
                </Space>
              )}
            />
          )}
        </Space>
      </Card>

      {!current || !outputs ? (
        <Card>
          <Empty description="还没有生成平台文案">
            <Button type="primary" loading={generation.isPending} disabled={!writable || busy} onClick={() => generation.mutate({})}>
              一次生成四个平台
            </Button>
          </Empty>
        </Card>
      ) : (
        <>
          {dirty && <Alert type="warning" showIcon message="有尚未保存的人工修改；保存前不能重写或定稿，避免覆盖。" />}
          <Row gutter={[16, 16]}>
            {CONTENT_PLATFORMS.map((platform) => {
              const output = outputs[platform];
              const meta = CONTENT_PLATFORM_META[platform];
              return (
                <Col xs={24} xl={12} key={platform}>
                  <Card
                    title={<Space><span style={{ width: 10, height: 10, borderRadius: 10, background: meta.color }} />{meta.label}</Space>}
                    extra={<Button size="small" icon={<CopyOutlined />} onClick={() => void copy(platform)}>复制发布包</Button>}
                    styles={{ body: { padding: 16 } }}
                  >
                    <Space direction="vertical" size={12} style={{ width: "100%" }}>
                      {(platform === "bilibili" || platform === "xiaohongshu") && (
                        <div>
                          <Typography.Text type="secondary">标题</Typography.Text>
                          <Input disabled={!writable} value={output.title ?? ""} onChange={(event) => updateOutput(platform, { title: event.target.value || null })} />
                        </div>
                      )}
                      <div>
                        <Typography.Text type="secondary">正文</Typography.Text>
                        <Input.TextArea disabled={!writable} rows={8} value={output.body} onChange={(event) => updateOutput(platform, { body: event.target.value })} />
                      </div>
                      <div>
                        <Typography.Text type="secondary">话题标签</Typography.Text>
                        <Input disabled={!writable} value={output.hashtags.join("，")} onChange={(event) => updateOutput(platform, { hashtags: parseHashtags(event.target.value) })} placeholder="用逗号或 # 分隔" />
                      </div>
                      <div>
                        <Typography.Text type="secondary">发布提示（仅运营可见）</Typography.Text>
                        <Input.TextArea disabled={!writable} rows={2} value={output.publishTip} onChange={(event) => updateOutput(platform, { publishTip: event.target.value })} />
                      </div>
                      {output.qualityWarnings.length > 0 && (
                        <Alert type="warning" showIcon message="平台适配提醒" description={output.qualityWarnings.join("；")} />
                      )}
                      <Space.Compact style={{ width: "100%" }}>
                        <Input
                          value={rewriteInstructions[platform] ?? ""}
                          onChange={(event) => setRewriteInstructions((previous) => ({ ...previous, [platform]: event.target.value }))}
                          placeholder={`告诉缈缈怎么改这份${meta.label}文案`}
                          disabled={!writable || dirty || busy}
                          onPressEnter={() => rewrite(platform)}
                        />
                        <Button loading={generation.isPending} disabled={!writable || dirty || busy} onClick={() => rewrite(platform)}>按意见重写</Button>
                      </Space.Compact>
                    </Space>
                  </Card>
                </Col>
              );
            })}
          </Row>

          <Card styles={{ body: { padding: 16 } }}>
            <Space wrap style={{ width: "100%", justifyContent: "space-between" }}>
              <Input
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder="填写本次保存或定稿的原因"
                disabled={!writable}
                maxLength={200}
                style={{ minWidth: 320, flex: 1 }}
              />
              <Space>
                <Button icon={<SaveOutlined />} type={dirty ? "primary" : "default"} disabled={!writable || !dirty || !reason.trim() || busy} loading={save.isPending} onClick={() => save.mutate()}>
                  保存人工修改
                </Button>
                <Button icon={<CheckOutlined />} disabled={!writable || dirty || !reason.trim() || busy || draft.status === "finalized"} loading={finalize.isPending} onClick={() => finalize.mutate()}>
                  确认定稿
                </Button>
              </Space>
            </Space>
          </Card>
        </>
      )}

      <Drawer title="版本历史" width={900} open={historyOpen} onClose={() => setHistoryOpen(false)}>
        {revisions.error ? <Alert type="error" showIcon message={(revisions.error as Error).message} /> : (
          <Table<ContentRevision>
            size="small"
            rowKey="id"
            loading={revisions.isLoading}
            columns={historyColumns}
            dataSource={revisions.data?.items ?? []}
            pagination={false}
            scroll={{ x: 850 }}
          />
        )}
      </Drawer>
    </Space>
  );
}
